import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { platformAPI } from '../../services/api';
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Twitter, Youtube, MessageCircle, Send, CheckCircle, Loader2 } from 'lucide-react';
import appConfig from '../../config/appConfig';
import SEO from '../../components/SEO';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:7000/api';

const EnquiryForm = () => {
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !contact.trim() || !message.trim()) {
      setError('All fields are required');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await fetch(API_BASE + '/users/enquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, contact, message })
      });
      setSuccess(true);
    } catch (err) {
      setError('Failed to send. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Enquiry Sent!</h3>
        <p className="text-sm text-gray-500 mb-4">We'll contact you soon.</p>
        <button onClick={() => { setSuccess(false); setName(''); setContact(''); setMessage(''); }} className="text-sm text-blue-600 hover:underline">Send another</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter your name"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Phone or Email *</label>
        <input
          type="text"
          value={contact}
          onChange={(e) => setContact(e.target.value)}
          placeholder="Enter your phone number or email"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Write your enquiry here..."
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          required
        />
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <button
        type="submit"
        disabled={loading}
        className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white text-sm font-medium rounded-lg transition-colors"
      >
        {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</> : <><Send className="h-4 w-4" /> Send Enquiry</>}
      </button>
    </form>
  );
};

const Support = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    platformAPI.getSettings()
      .then(res => setSettings(res.data?.settings))
      .catch(err => console.error('Error loading settings:', err))
      .finally(() => setLoading(false));
  }, []);

  const contact = settings?.supportContact || {};
  const social = settings?.socialLinks || {};

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO title="Support & Contact" description={`Need help? Contact ${appConfig.name} support by phone, email or social media.`} path="/support" />
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/browse" className="text-xl font-bold text-blue-600">{appConfig.name}</Link>
          <div className="flex gap-4 text-sm">
            <Link to="/about" className="text-gray-600 hover:text-blue-600">About</Link>
            <Link to="/terms" className="text-gray-600 hover:text-blue-600">Terms</Link>
            <Link to="/privacy" className="text-gray-600 hover:text-blue-600">Privacy</Link>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-10">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Support & Contact</h1>
        <p className="text-gray-600 mb-8">We're here to help. Reach out to us through any of the channels below.</p>

        {loading ? (
          <div className="text-center py-10 text-gray-500">Loading...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Contact Cards */}
            {contact.phone && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 flex items-start gap-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <Phone className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                  <a href={`tel:${contact.phone}`} className="text-blue-600 hover:underline">{contact.phone}</a>
                </div>
              </div>
            )}

            {contact.email && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 flex items-start gap-4">
                <div className="p-3 bg-green-50 rounded-lg">
                  <Mail className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                  <a href={`mailto:${contact.email}`} className="text-blue-600 hover:underline">{contact.email}</a>
                </div>
              </div>
            )}

            {contact.address && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 flex items-start gap-4">
                <div className="p-3 bg-purple-50 rounded-lg">
                  <MapPin className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Address</h3>
                  <p className="text-gray-600">{contact.address}</p>
                </div>
              </div>
            )}

            {contact.workingHours && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 flex items-start gap-4">
                <div className="p-3 bg-orange-50 rounded-lg">
                  <Clock className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Working Hours</h3>
                  <p className="text-gray-600">{contact.workingHours}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Social Links */}
        {(social.facebook || social.instagram || social.twitter || social.youtube || social.whatsapp) && (
          <div className="mt-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Follow Us</h2>
            <div className="flex flex-wrap gap-4">
              {social.facebook && (
                <a href={social.facebook} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-blue-50 transition-colors">
                  <Facebook className="h-5 w-5 text-blue-600" />
                  <span className="text-sm text-gray-700">Facebook</span>
                </a>
              )}
              {social.instagram && (
                <a href={social.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-pink-50 transition-colors">
                  <Instagram className="h-5 w-5 text-pink-600" />
                  <span className="text-sm text-gray-700">Instagram</span>
                </a>
              )}
              {social.twitter && (
                <a href={social.twitter} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-sky-50 transition-colors">
                  <Twitter className="h-5 w-5 text-sky-500" />
                  <span className="text-sm text-gray-700">Twitter</span>
                </a>
              )}
              {social.youtube && (
                <a href={social.youtube} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-red-50 transition-colors">
                  <Youtube className="h-5 w-5 text-red-600" />
                  <span className="text-sm text-gray-700">YouTube</span>
                </a>
              )}
              {social.whatsapp && (
                <a href={`https://wa.me/${social.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-green-50 transition-colors">
                  <MessageCircle className="h-5 w-5 text-green-600" />
                  <span className="text-sm text-gray-700">WhatsApp</span>
                </a>
              )}
            </div>
          </div>
        )}

        {/* Enquiry Form */}
        <div className="mt-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Send us an Enquiry</h2>
          <EnquiryForm />
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 bg-white mt-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex flex-wrap items-center justify-between gap-2 text-sm text-gray-500">
          <span>{appConfig.copyright}</span>
          <div className="flex gap-4">
            <Link to="/support" className="hover:text-blue-600">Support</Link>
            <Link to="/terms" className="hover:text-blue-600">Terms</Link>
            <Link to="/privacy" className="hover:text-blue-600">Privacy</Link>
            <Link to="/refund-policy" className="hover:text-blue-600">Refund Policy</Link>
            <Link to="/about" className="hover:text-blue-600">About</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Support;
